import { useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Trash2, Percent, DollarSign, Calendar, Tag } from "lucide-react";
import { usePricingRules, useDeletePricingRule, PricingRule } from "@/hooks/useProducts";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

export function GlobalPricingRulesTable() {
  const { data: allRules, isLoading } = usePricingRules();
  const deleteRule = useDeletePricingRule();
  const queryClient = useQueryClient();
  const [deletingRule, setDeletingRule] = useState<PricingRule | null>(null);

  const globalRules = allRules?.filter((r) => !r.product_id) || [];

  const handleDelete = async () => {
    if (deletingRule) {
      await deleteRule.mutateAsync(deletingRule.id);
      setDeletingRule(null);
    }
  };

  const handleToggleActive = async (rule: PricingRule) => {
    try {
      const { error } = await supabase
        .from("pricing_rules")
        .update({ is_active: !rule.is_active })
        .eq("id", rule.id);

      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ["pricing-rules"] });
      toast.success(`Rule ${!rule.is_active ? "activated" : "deactivated"}`);
    } catch (error) {
      console.error("Error toggling rule:", error);
      toast.error("Failed to update rule");
    }
  };

  const formatRuleValue = (rule: PricingRule) => {
    if (rule.value_type === "percentage") {
      return `${rule.value}%`;
    }
    return `$${rule.value.toFixed(2)}`;
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  if (globalRules.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Tag className="h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold">No global pricing rules</h3>
        <p className="text-muted-foreground">Add rules that apply across all products.</p>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-lg border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead>Rule Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Value</TableHead>
              <TableHead>Conditions</TableHead>
              <TableHead>Validity</TableHead>
              <TableHead>Priority</TableHead>
              <TableHead>Active</TableHead>
              <TableHead className="w-[60px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {globalRules.map((rule) => {
              const condition = rule.condition as { min_quantity?: number; max_quantity?: number } | null;
              return (
                <TableRow key={rule.id} className="group">
                  <TableCell className="font-medium">{rule.name}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">
                      {rule.rule_type}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {rule.value_type === "percentage" ? (
                        <Percent className="h-3 w-3 text-muted-foreground" />
                      ) : (
                        <DollarSign className="h-3 w-3 text-muted-foreground" />
                      )}
                      <span className={rule.rule_type === "discount" ? "text-green-500" : "text-amber-500"}>
                        {rule.rule_type === "discount" ? "-" : "+"}
                        {formatRuleValue(rule)}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {condition?.min_quantity || condition?.max_quantity ? (
                      <span>
                        {condition.min_quantity && `Min: ${condition.min_quantity}`}
                        {condition.min_quantity && condition.max_quantity && " • "}
                        {condition.max_quantity && `Max: ${condition.max_quantity}`}
                      </span>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell>
                    {rule.valid_from || rule.valid_until ? (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        {rule.valid_from && format(new Date(rule.valid_from), "MMM d")}
                        {rule.valid_from && rule.valid_until && " - "}
                        {rule.valid_until && format(new Date(rule.valid_until), "MMM d")}
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">Always</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">{rule.priority}</Badge>
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={rule.is_active}
                      onCheckedChange={() => handleToggleActive(rule)}
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setDeletingRule(rule)}
                      className="opacity-0 group-hover:opacity-100 text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!deletingRule} onOpenChange={(open) => !open && setDeletingRule(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Pricing Rule</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deletingRule?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
